import { FormDialog } from "@tlc/ui/dialog/FormDialog";
import React, { useCallback, useState } from "react";
import { useDialog } from "@tlc/ui/dialog/useDialog";
import { FormProvider, useForm } from "react-hook-form";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { FormTextField } from "@tlc/forms/FormTextField";
import { Alert, Stack } from "@mui/material";
import { useToast } from "@tlc/ui/toast";
import { axios } from "@tlc/ls2pac/components/AxiosInstanceManager";
import { EMAIL_PATTERN } from "../../obr/EmailField"; // TODO move to forms package
import { useAnalytics } from "../../../../analytics/useGoogleAnalytics";
import { AllListsSummary, ListType } from "../ListService";
import { useAccount } from "@tlc/ls2pac/components/Account";

interface ShareListFormFields {
  friends: string;
  note: string;
}

export function useShareListDialog() {
  const { dialogProps, openDialog } = useDialog();
  const { translate } = useLanguage();
  const formMethods = useForm<ShareListFormFields>();
  const { showToast } = useToast();
  const { handleSubmit, setFocus, reset } = formMethods;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [apiError, setApiError] = useState<boolean>(false);
  const shareListFormId = "share-list-form";
  const [listSummary, setListSummary] = useState<AllListsSummary | null>(null);
  const { trackEvent } = useAnalytics();

  const openShareListDialog = useCallback(
    (summary: AllListsSummary) => {
      reset();
      setListSummary(summary);
      openDialog();
      setTimeout(() => setFocus("friends"));
    },
    [openDialog, setFocus, reset],
  );

  const onSubmit = async (data: ShareListFormFields) => {
    try {
      setIsSubmitting(true);
      setApiError(false);
      const response = await axios.post(
        `list/static/${listSummary?.id}/email`,
        data,
      );
      trackEvent("list_share", {
        list_type: listSummary?.type,
        item_count: listSummary?.count,
      });
      console.log(response);
      showToast(translate("sharedListSuccessMessage"), "success");
      dialogProps.onClose();
    } catch (error: unknown) {
      setApiError(true);
      setFocus("friends"); // error message is placed above this field
    } finally {
      setIsSubmitting(false);
    }
  };

  const ShareListDialog = (
    <FormDialog
      isSubmitting={isSubmitting}
      formId={shareListFormId}
      confirmButtonText={translate("send")}
      {...dialogProps}
      headline={translate("shareControlHeader")}
      maxWidth="sm"
    >
      <form noValidate id={shareListFormId} onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...formMethods}>
          <Stack spacing={2} mt={2}>
            {apiError && (
              <Alert severity="error">
                {translate("sharedListErrorMessage")}
              </Alert>
            )}
            <FormTextField<ShareListFormFields>
              disabled={isSubmitting}
              maxLength={1000}
              fullWidth
              type="email"
              sx={{ width: "100%", maxWidth: "auto" }}
              required
              requiredErrorMessage={translate("atLeastOneRequired", {
                SINGULAR_LABEL: translate(
                  "eResourceHoldsEmailLabel",
                ).toLocaleLowerCase(),
              })}
              options={{
                validate: (value) => {
                  const emails = value.split(",").map((email) => email.trim());

                  if (emails.some((email) => !EMAIL_PATTERN.test(email))) {
                    return translate("shareInvalidEmailAddresses");
                  }
                  return true;
                },
              }}
              label={translate("shareWithEmailsLabel")}
              name="friends"
              helperText={translate("shareWithEmailsSecondaryLabel")}
            />
            <FormTextField<ShareListFormFields>
              disabled={isSubmitting}
              fullWidth
              sx={{ width: "100%", maxWidth: "auto" }}
              maxLength={5000}
              multiline
              minRows={3}
              label={translate("shareEmailMessageLabel")}
              name="note"
            />
          </Stack>
        </FormProvider>
      </form>
    </FormDialog>
  );
  return {
    openShareListDialog,
    ShareListDialog,
  };
}
